import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, Users, MapPin, Globe, GraduationCap, Briefcase, Heart, Sparkles,
  X, RefreshCw, Info, Ruler,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell,
  ComposedChart, Line, ReferenceLine,
} from 'recharts';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const PIE_COLORS = ['#D98A94', '#F5B0B8', '#E8A5AE', '#C0727C', '#F0C3C9', '#B85E68', '#EFB4BC', '#A85560'];

function slugify(s) {
  return String(s || '').toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function StatCard({ label, value, sub, tone = 'rose', icon: Icon, testId }) {
  const tones = {
    rose:  'from-[#FFF7F5] to-white border-[#D98A94]/30 text-[#C0727C]',
    blue:  'from-blue-50 to-white border-blue-200 text-blue-700',
    green: 'from-green-50 to-white border-green-200 text-green-700',
    amber: 'from-amber-50 to-white border-amber-200 text-amber-700',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${tones[tone]} px-3 py-2.5`} data-testid={testId}>
      {Icon && <Icon className="w-3.5 h-3.5 mb-1 opacity-80" />}
      <div className="text-[10px] font-bold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="glass-card rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-3">
        <Icon className="w-3.5 h-3.5 text-[#D98A94]" />
        <h4 className="text-xs font-bold t-text uppercase tracking-widest">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function HorizontalBarList({ items, total }) {
  const max = Math.max(1, ...items.map(i => i.count));
  return (
    <div className="space-y-1.5">
      {items.map(it => {
        const pct = total ? Math.round((it.count / total) * 100) : 0;
        return (
          <div key={it.name} className="flex items-center gap-2 text-xs">
            <span className="w-24 t-text-secondary truncate font-medium">{it.name}</span>
            <div className="flex-1 h-2 bg-[#D98A94]/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#D98A94] rounded-full" style={{ width: `${(it.count / max) * 100}%` }} />
            </div>
            <span className="t-text font-bold w-14 text-right">{it.count} <span className="t-text-muted font-normal">({pct}%)</span></span>
          </div>
        );
      })}
    </div>
  );
}

function LifestyleRow({ label, pct, positive, icon }) {
  const c = positive ? '#16A34A' : pct > 40 ? '#F59E0B' : '#D98A94';
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-base">{icon}</span>
      <span className="w-24 t-text-secondary font-medium">{label}</span>
      <div className="flex-1 h-2 bg-[#D98A94]/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct)}%`, background: c }} />
      </div>
      <span className="font-bold w-10 text-right" style={{ color: c }}>{pct}%</span>
    </div>
  );
}

export default function MemberStatsSheet({ open, onClose, category, lang }) {
  const isID = lang !== 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!category) return;
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/categories/stats/${slugify(category)}`;
      const res = await axios.get(url);
      setData(res.data);
    } catch (err) {
      console.warn('stats fetch failed', err);
    }
    setLoading(false);
  }, [category]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const total = data?.total || 0;
  const age = data?.age;
  const mean = age?.mean || 0;
  const stddev = age?.stddev || 0;

  // Merge histogram + normal_curve into one dataset for ComposedChart.
  // Each histogram bucket becomes 5 rows in the merged dataset (one per year),
  // with `count` non-null only at the bucket's center year for visual anchoring,
  // while the normal-curve line is defined at every year point.
  const curveData = React.useMemo(() => {
    if (!age || !age.normal_curve?.length) return [];
    // Build a lookup: for each x (age), find which histogram bucket contains it
    const histLookup = new Map();
    (age.histogram || []).forEach((b) => {
      const [lo, hi] = b.label.includes('+')
        ? [parseInt(b.label, 10), 99]
        : b.label.split('-').map(v => parseInt(v, 10));
      for (let x = lo; x <= Math.min(hi, 90); x++) {
        histLookup.set(x, b.count / (hi - lo + 1)); // spread bucket count evenly across years for bar-per-year
      }
    });
    return age.normal_curve.map(pt => ({
      x: pt.x,
      curve: pt.y,
      bar: histLookup.get(pt.x) || 0,
    }));
  }, [age]);

  const percentileLadder = age?.percentiles ? [
    { p: '10%', v: age.percentiles.p10, hint: isID ? '10% termuda' : 'Youngest 10%' },
    { p: '25%', v: age.percentiles.p25, hint: 'Q1' },
    { p: '50%', v: age.percentiles.p50, hint: 'Median' },
    { p: '75%', v: age.percentiles.p75, hint: 'Q3' },
    { p: '90%', v: age.percentiles.p90, hint: isID ? '10% tertua' : 'Oldest 10%' },
  ] : [];

  const skewLabel = (() => {
    const s = age?.skewness || 0;
    if (s > 0.5) return { txt: isID ? 'Miring Kanan (+)' : 'Right-Skew (+)', c: 'text-amber-700', tone: 'amber' };
    if (s < -0.5) return { txt: isID ? 'Miring Kiri (–)' : 'Left-Skew (–)', c: 'text-blue-700', tone: 'blue' };
    return { txt: isID ? 'Simetris' : 'Symmetric', c: 'text-green-700', tone: 'green' };
  })();

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto glass-card border-l t-border p-0"
        data-testid="member-stats-sheet"
      >
        <SheetHeader className="sticky top-0 z-10 bg-white/80 backdrop-blur-md px-5 py-4 border-b t-border">
          <div className="flex items-center justify-between gap-2">
            <SheetTitle className="text-base font-bold t-text flex items-center gap-1.5">
              <BarChart3 className="w-4 h-4 text-[#D98A94]" />
              {isID ? 'Statistik Member' : 'Member Statistics'}
              {category && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#D98A94]/10 text-[#C0727C] text-[10px] font-bold">{category}</span>}
            </SheetTitle>
            <button onClick={onClose} data-testid="member-stats-close" className="p-2 rounded-full hover:bg-white/60 t-text-secondary">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[11px] t-text-muted text-left">
            {isID
              ? 'Data agregat komunitas terkini. Update otomatis setiap kali kamu buka panel ini.'
              : 'Live aggregate community data. Refreshes every time you open this panel.'}
          </p>
        </SheetHeader>

        <div className="p-4 space-y-4">
          {loading && (
            <div className="glass-card rounded-2xl p-10 text-center t-text-muted text-sm" data-testid="member-stats-loading">
              <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
              {isID ? 'Menghitung statistik…' : 'Computing…'}
            </div>
          )}

          {!loading && data && data.total === 0 && (
            <div className="glass-card rounded-2xl p-8 text-center t-text-muted text-sm">
              {isID ? 'Belum ada data untuk kategori ini.' : 'No data for this category yet.'}
            </div>
          )}

          {!loading && data && data.total > 0 && age && (
            <>
              {/* Top-line KPIs */}
              <div className="grid grid-cols-3 gap-2" data-testid="member-stats-kpis">
                <StatCard label={isID ? 'Total' : 'Total'} value={total} tone="rose" icon={Users} testId="stat-total" />
                <StatCard label={isID ? 'Pria' : 'Male'} value={`${total ? Math.round((data.male_count / total) * 100) : 0}%`} sub={`${data.male_count}`} tone="blue" testId="stat-male" />
                <StatCard label={isID ? 'Wanita' : 'Female'} value={`${total ? Math.round((data.female_count / total) * 100) : 0}%`} sub={`${data.female_count}`} tone="rose" testId="stat-female" />
              </div>

              {/* Statistical deep-dive on age */}
              <Section icon={TrendingUp} title={isID ? 'Distribusi Usia + Kurva Normal' : 'Age Distribution + Normal Curve'} testId="member-stats-age-section">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                  <StatCard label="Mean" value={`${age.mean}`} sub={isID ? 'rata-rata' : 'average'} tone="rose" testId="stat-mean" />
                  <StatCard label="Median" value={`${age.median}`} sub="p50" tone="slate" testId="stat-median" />
                  <StatCard label="Mode" value={`${age.mode}`} sub={isID ? 'paling sering' : 'most freq.'} tone="slate" testId="stat-mode" />
                  <StatCard label="σ (SD)" value={`±${age.stddev}`} sub={isID ? 'sebaran' : 'spread'} tone="blue" testId="stat-stddev" />
                </div>

                {/* Bell curve overlay */}
                {curveData.length > 0 && (
                  <div className="w-full" style={{ height: 220 }} data-testid="member-stats-normal-curve">
                    <ResponsiveContainer>
                      <ComposedChart data={curveData} margin={{ top: 12, right: 8, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F5E7EA" />
                        <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#8A6E72' }} label={{ value: isID ? 'usia (tahun)' : 'age (years)', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#8A6E72' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#8A6E72' }} />
                        <Tooltip
                          contentStyle={{ background: '#fff', border: '1px solid #F0C3C9', borderRadius: 8, fontSize: 12 }}
                          formatter={(v, k) => k === 'curve' ? [Number(v).toFixed(2), isID ? 'kurva normal' : 'normal curve'] : [Math.round(Number(v)), isID ? 'member/th' : 'members/yr']}
                          labelFormatter={(x) => `${isID ? 'Usia' : 'Age'}: ${x}`}
                        />
                        <Bar dataKey="bar" fill="#F5E7EA" stroke="#D98A94" strokeWidth={0.5} />
                        <Line type="monotone" dataKey="curve" stroke="#C0727C" strokeWidth={2.5} dot={false} />
                        <ReferenceLine x={Math.round(mean)} stroke="#16A34A" strokeDasharray="4 4" label={{ value: 'μ', position: 'top', fontSize: 11, fill: '#16A34A', fontWeight: 700 }} />
                        <ReferenceLine x={Math.round(mean - stddev)} stroke="#2563EB" strokeDasharray="2 4" label={{ value: '−σ', position: 'top', fontSize: 10, fill: '#2563EB' }} />
                        <ReferenceLine x={Math.round(mean + stddev)} stroke="#2563EB" strokeDasharray="2 4" label={{ value: '+σ', position: 'top', fontSize: 10, fill: '#2563EB' }} />
                        <ReferenceLine x={Math.round(mean - 2 * stddev)} stroke="#93C5FD" strokeDasharray="1 4" label={{ value: '−2σ', position: 'top', fontSize: 9, fill: '#93C5FD' }} />
                        <ReferenceLine x={Math.round(mean + 2 * stddev)} stroke="#93C5FD" strokeDasharray="1 4" label={{ value: '+2σ', position: 'top', fontSize: 9, fill: '#93C5FD' }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Skewness + Kurtosis chips */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <StatCard label={isID ? 'Skewness' : 'Skewness'} value={`${age.skewness}`} sub={skewLabel.txt} tone={skewLabel.tone} testId="stat-skewness" />
                  <StatCard label={isID ? 'Kurtosis (ekses)' : 'Kurtosis (excess)'} value={`${age.kurtosis}`} sub={age.kurtosis > 1 ? (isID ? 'runcing (leptokurtik)' : 'peaky (leptokurtic)') : age.kurtosis < -1 ? (isID ? 'datar (platikurtik)' : 'flat (platykurtic)') : (isID ? 'seperti normal' : 'near-normal')} tone="slate" testId="stat-kurtosis" />
                </div>

                {/* Interpretation */}
                <div className="mt-3 p-3 rounded-xl bg-[#D98A94]/6 border border-[#D98A94]/20" data-testid="member-stats-interpretation">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold text-[#C0727C] uppercase tracking-widest mb-1.5">
                    <Info className="w-3 h-3" />
                    {isID ? 'Penjelasan Statistik' : 'Statistical Interpretation'}
                  </div>
                  <p className="text-xs t-text leading-relaxed">{isID ? age.interpretation?.id : age.interpretation?.en}</p>
                </div>

                {/* Percentiles */}
                {percentileLadder.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10px] font-bold t-text-muted uppercase tracking-widest mb-1.5">
                      {isID ? 'Ladder Persentil' : 'Percentile Ladder'}
                    </div>
                    <div className="grid grid-cols-5 gap-1.5">
                      {percentileLadder.map(x => (
                        <div key={x.p} className="rounded-lg bg-white border t-border px-2 py-1.5 text-center">
                          <div className="text-[9px] t-text-muted font-bold">{x.p}</div>
                          <div className="text-sm font-bold t-text">{x.v}</div>
                          <div className="text-[9px] t-text-muted italic">{x.hint}</div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] t-text-muted mt-1.5">
                      {isID
                        ? 'Contoh: "p25 = 27" berarti 25% member berusia ≤ 27 tahun.'
                        : 'Read: "p25 = 27" means 25% of members are ≤ 27 years old.'}
                    </p>
                  </div>
                )}
              </Section>

              {/* Domicile */}
              {data.by_country?.length > 0 && (
                <Section icon={Globe} title={isID ? 'Domisili · Negara' : 'By Country'} testId="member-stats-country">
                  <HorizontalBarList items={data.by_country.slice(0, 6)} total={total} />
                </Section>
              )}
              {data.by_city?.length > 0 && (
                <Section icon={MapPin} title={isID ? 'Kota Populer' : 'Top Cities'} testId="member-stats-city">
                  <HorizontalBarList items={data.by_city.slice(0, 6)} total={total} />
                </Section>
              )}

              {/* Education */}
              {data.by_education?.length > 0 && (
                <Section icon={GraduationCap} title={isID ? 'Pendidikan' : 'Education'} testId="member-stats-education">
                  <div style={{ height: 200 }}>
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={data.by_education.slice(0, 8)} dataKey="count" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                          {data.by_education.slice(0, 8).map((d, i) => <Cell key={d.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: '#fff', border: '1px solid #F0C3C9', borderRadius: 8, fontSize: 12 }} formatter={(v, _n, p) => [`${v} (${total ? Math.round((v / total) * 100) : 0}%)`, p.payload.name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-[11px] mt-1">
                    {data.by_education.slice(0, 8).map((d, i) => (
                      <div key={d.name} className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="t-text-secondary truncate">{d.name}</span>
                        <span className="t-text-muted ml-auto">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Occupation */}
              {data.by_occupation?.length > 0 && (
                <Section icon={Briefcase} title={isID ? 'Pekerjaan' : 'Occupation'} testId="member-stats-occupation">
                  <HorizontalBarList items={data.by_occupation.slice(0, 6)} total={total} />
                </Section>
              )}

              {/* Marriage status */}
              {data.by_marriage_status?.length > 0 && (
                <Section icon={Heart} title={isID ? 'Status Pernikahan' : 'Marital Status'} testId="member-stats-marriage">
                  <HorizontalBarList items={data.by_marriage_status} total={total} />
                </Section>
              )}

              {/* MBTI */}
              {data.by_mbti?.length > 0 && (
                <Section icon={Sparkles} title="MBTI" testId="member-stats-mbti">
                  <HorizontalBarList items={data.by_mbti.slice(0, 8)} total={total} />
                </Section>
              )}

              {/* Lifestyle */}
              {data.lifestyle && (
                <Section icon={Ruler} title={isID ? 'Gaya Hidup' : 'Lifestyle'} testId="member-stats-lifestyle">
                  <div className="space-y-1.5">
                    <LifestyleRow label={isID ? 'Rokok' : 'Smokes'} pct={data.lifestyle.smokes.yes_pct} icon="🚬" />
                    <LifestyleRow label="Vape" pct={data.lifestyle.vapes.yes_pct} icon="💨" />
                    <LifestyleRow label={isID ? 'Tato' : 'Tattoo'} pct={data.lifestyle.tattoo.yes_pct} icon="🎨" />
                    <LifestyleRow label={isID ? 'Rutin ibadah' : 'Regular worship'} pct={data.lifestyle.routine_worship.yes_pct} positive icon="🕊️" />
                  </div>
                </Section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
